const DATABASE = 'vision-image-blobs';
const METADATA = 'metadata';
const BLOBS = 'blobs';
const MAX_BYTES = 100 * 1024 * 1024;
const MAX_ENTRIES = 500;
let pending = Promise.resolve();

function validOwner(owner) {
  return typeof owner === 'string' && owner.length > 0;
}

function openDatabase() {
  return new Promise((resolve) => {
    let abandoned = false;
    try {
      if (!globalThis.indexedDB) return resolve(null);
      const request = globalThis.indexedDB.open(DATABASE, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        const metadata = db.createObjectStore(METADATA, { keyPath: ['owner', 'key'] });
        metadata.createIndex('owner', 'owner');
        db.createObjectStore(BLOBS, { keyPath: ['owner', 'key'] });
      };
      request.onsuccess = () => {
        if (abandoned) {
          request.result.close();
        } else {
          request.result.onversionchange = () => request.result.close();
          resolve(request.result);
        }
      };
      request.onerror = request.onblocked = () => {
        abandoned = true;
        resolve(null);
      };
    } catch {
      resolve(null);
    }
  });
}

function transact(mode, fallback, operation) {
  const run = pending.then(async () => {
    const db = await openDatabase();
    if (!db) return fallback;
    try {
      return await new Promise((resolve) => {
        const tx = db.transaction([METADATA, BLOBS], mode);
        let result = fallback;
        tx.oncomplete = () => resolve(result);
        tx.onabort = tx.onerror = () => resolve(fallback);
        operation(tx, (value) => { result = value; });
      });
    } finally {
      db.close();
    }
  }).catch(() => fallback);
  pending = run.then(() => {});
  return run;
}

export function readImageBlob(owner, key, version, now = Date.now()) {
  if (!validOwner(owner) || typeof key !== 'string' || !Number.isFinite(now)) return Promise.resolve(null);
  return transact('readonly', null, (tx, setResult) => {
    const request = tx.objectStore(METADATA).get([owner, key]);
    request.onsuccess = () => {
      const entry = request.result;
      if (!entry || entry.version !== version || !Number.isFinite(entry.expiresAt) || entry.expiresAt <= now) return;
      const content = tx.objectStore(BLOBS).get([owner, key]);
      content.onsuccess = () => {
        if (content.result?.blob) setResult({ blob: content.result.blob, expiresAt: entry.expiresAt });
      };
    };
  });
}

export function writeImageBlob(owner, key, version, blob, expiresAt) {
  if (!validOwner(owner) || typeof key !== 'string' || !key || typeof version !== 'string'
    || !globalThis.Blob || !(blob instanceof globalThis.Blob)
    || blob.size > MAX_BYTES || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    return Promise.resolve();
  }
  return transact('readwrite', undefined, (tx) => {
    const metadata = tx.objectStore(METADATA);
    const blobs = tx.objectStore(BLOBS);
    const request = metadata.index('owner').getAll(owner);
    request.onsuccess = () => {
      try {
        const entries = [];
        const now = Date.now();
        let bytes = blob.size;
        let order = 0;
        for (const entry of request.result) {
          order = Math.max(order, entry.order);
          if (entry.expiresAt <= now || entry.key === key) {
            metadata.delete([owner, entry.key]);
            blobs.delete([owner, entry.key]);
          } else {
            entries.push(entry);
            bytes += entry.size;
          }
        }
        if (expiresAt <= now) return;
        entries.sort((a, b) => a.order - b.order);
        while (entries.length >= MAX_ENTRIES || bytes > MAX_BYTES) {
          const entry = entries.shift();
          metadata.delete([owner, entry.key]);
          blobs.delete([owner, entry.key]);
          bytes -= entry.size;
        }
        metadata.put({ owner, key, version, expiresAt, size: blob.size, order: order + 1 });
        blobs.put({ owner, key, blob });
      } catch {
        tx.abort();
      }
    };
  });
}

export function deleteImageBlob(owner, key) {
  if (!validOwner(owner) || typeof key !== 'string') return Promise.resolve();
  return transact('readwrite', undefined, (tx) => {
    tx.objectStore(METADATA).delete([owner, key]);
    tx.objectStore(BLOBS).delete([owner, key]);
  });
}

export function clearImageBlobs(owner) {
  if (!validOwner(owner)) return Promise.resolve();
  return transact('readwrite', undefined, (tx) => {
    const metadata = tx.objectStore(METADATA);
    const request = metadata.index('owner').getAllKeys(owner);
    request.onsuccess = () => {
      for (const key of request.result) {
        metadata.delete(key);
        tx.objectStore(BLOBS).delete(key);
      }
    };
  });
}
